import React, { useState, useEffect } from 'react';
import {
    Truck,
    MapPin,
    Package,
    Calendar,
    DollarSign,
    CheckCircle,
    Loader,
    ArrowLeft,
    Home,
    ChevronRight,
    Phone,
    Mail,
    Users,
    Twitter,
    Linkedin,
    Facebook,
    Instagram,
    AlertCircle,
    Shield,
    SprayCan, // For fumigation
    Brush, // For cleaning
    List // For inventory management
} from 'lucide-react';

// Mock API Service for Moving Services
class MovingServicesAPI {
    static async getCostEstimate({ from, to, moveSize, additionalServices }) {
        try {
            // Placeholder for API call
            // const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/api/moving/estimate`, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         ...(localStorage.getItem('authToken') && {
            //             'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            //         })
            //     },
            //     body: JSON.stringify({ from, to, moveSize, additionalServices })
            // });
            // if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            // return await response.json();

            // Default values for now
            const baseCosts = {
                small: 5000,
                medium: 10000,
                large: 15000
            };
            const serviceCosts = {
                packing: 1000,
                furniture: 1000,
                storage: 1000,
                cleaning: 1500,
                fumigation: 2000,
                insurance: 2500,
                inventory: 1200
            };
            const serviceFee = additionalServices.reduce((total, service) => total + (serviceCosts[service] || 0), 0);

            return {
                success: true,
                estimate: {
                    baseCost: baseCosts[moveSize] || 5000,
                    distanceFee: 2000,
                    serviceFee,
                    total: baseCosts[moveSize] + 2000 + serviceFee,
                    currency: 'KES'
                }
            };
        } catch (error) {
            console.error('Error fetching cost estimate:', error);
            throw error;
        }
    }

    static async submitBooking(bookingDetails) {
        try {
            // Placeholder for API call
            // const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/api/moving/book`, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         ...(localStorage.getItem('authToken') && {
            //             'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            //         })
            //     },
            //     body: JSON.stringify(bookingDetails)
            // });
            // if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            // return await response.json();

            // Default response for now
            return {
                success: true,
                bookingId: 'BOOK123456',
                message: 'Booking submitted successfully!'
            };
        } catch (error) {
            console.error('Error submitting booking:', error);
            throw error;
        }
    }
}

const MovingServices = () => {
    const [formData, setFormData] = useState({
        from: '',
        to: '',
        moveSize: 'small',
        additionalServices: [],
        moveDate: '',
        fullName: '',
        email: '',
        phone: '',
        notes: ''
    });
    const [estimate, setEstimate] = useState(null);
    const [isEstimating, setIsEstimating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [bookingSuccess, setBookingSuccess] = useState(null);

    // Options for form fields
    const moveSizeOptions = [
        { value: 'small', label: 'Small (Studio/1-Bedroom)', baseCost: 5000 },
        { value: 'medium', label: 'Medium (2-3 Bedrooms)', baseCost: 10000 },
        { value: 'large', label: 'Large (4+ Bedrooms)', baseCost: 15000 }
    ];

    const additionalServicesOptions = [
        { id: 'packing', label: 'Packing Service', cost: 1000, icon: Package },
        { id: 'furniture', label: 'Furniture Assembly', cost: 1000, icon: Home },
        { id: 'storage', label: 'Storage', cost: 1000, icon: Package },
        { id: 'cleaning', label: 'Cleaning Service', cost: 1500, icon: Brush },
        { id: 'fumigation', label: 'Fumigation', cost: 2000, icon: SprayCan },
        { id: 'insurance', label: 'Moving Insurance', cost: 2500, icon: Shield },
        { id: 'inventory', label: 'Inventory Management', cost: 1200, icon: List }
    ];

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);

        // Only reset estimate for fields that affect cost
        if (['from', 'to', 'moveSize'].includes(field)) {
            setEstimate(null);
        }
    };

    // Handle additional services toggle
    const handleServiceToggle = (serviceId) => {
        setFormData(prev => ({
            ...prev,
            additionalServices: prev.additionalServices.includes(serviceId)
                ? prev.additionalServices.filter(id => id !== serviceId)
                : [...prev.additionalServices, serviceId]
        }));
        setEstimate(null); // Reset estimate when services change
    };

    // Fetch cost estimate
    const getCostEstimate = async () => {
        if (!formData.from || !formData.to) {
            setError('Please enter both starting and destination locations.');
            return;
        }

        setIsEstimating(true);
        setError(null);
        try {
            const response = await MovingServicesAPI.getCostEstimate({
                from: formData.from,
                to: formData.to,
                moveSize: formData.moveSize,
                additionalServices: formData.additionalServices
            });
            setEstimate(response.estimate);
        } catch (error) {
            setError('Failed to fetch cost estimate. Please try again.');
        } finally {
            setIsEstimating(false);
        }
    };

    // Submit booking
    const submitBooking = async () => {
        if (!formData.from || !formData.to || !formData.moveDate || !formData.fullName || !formData.email || !formData.phone) {
            setError('Please fill in all required fields.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await MovingServicesAPI.submitBooking(formData);
            setBookingSuccess(response);
            setFormData({
                from: '',
                to: '',
                moveSize: 'small',
                additionalServices: [],
                moveDate: '',
                fullName: '',
                email: '',
                phone: '',
                notes: ''
            });
            setEstimate(null);
        } catch (error) {
            setError('Failed to submit booking. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format currency
    const formatCurrency = (amount) => `KES ${amount.toLocaleString()}`;

    return (
        <div className="min-h-screen bg-karibu-100">
            {/* Hero Section */}
            <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-6">
                            <div className="p-4 bg-white/10 rounded-full">
                                <Truck className="w-12 h-12" />
                            </div>
                        </div>
                        <h1 className="font-display text-5xl font-bold mb-6">Moving Services</h1>
                        <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto leading-relaxed">
                            Plan your move with ease. Get an instant cost estimate and book our professional moving services to make your transition seamless.
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                        aria-label="Go to home"
                    >
                        <Home className="w-4 h-4 mr-1" />
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Moving Services</span>
                </div>
            </div>

            {/* Cost Estimator Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                    <h2 className="text-3xl font-bold text-soil-900 mb-6 text-center">Get Your Moving Cost Estimate</h2>
                    <p className="text-soil-600 text-center max-w-2xl mx-auto mb-8">
                        Enter your move details below to get an instant estimate for our professional moving services.
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Left: Form Inputs */}
                        <div className="space-y-6">
                            {/* From Location */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <MapPin className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">From</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.from}
                                    onChange={(e) => handleInputChange('from', e.target.value)}
                                    placeholder="Enter starting location (e.g., Westlands, Nairobi)"
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Starting location"
                                    required
                                />
                            </div>

                            {/* To Location */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <MapPin className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">To</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.to}
                                    onChange={(e) => handleInputChange('to', e.target.value)}
                                    placeholder="Enter destination (e.g., Kilimani, Nairobi)"
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Destination location"
                                    required
                                />
                            </div>

                            {/* Move Size */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <Package className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">Move Size</span>
                                </label>
                                <select
                                    value={formData.moveSize}
                                    onChange={(e) => handleInputChange('moveSize', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Select move size"
                                >
                                    {moveSizeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Additional Services */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <CheckCircle className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">Additional Services</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {additionalServicesOptions.map(service => {
                                        const Icon = service.icon;
                                        const isSelected = formData.additionalServices.includes(service.id);
                                        return (
                                            <button
                                                key={service.id}
                                                onClick={() => handleServiceToggle(service.id)}
                                                className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 text-sm ${
                                                    isSelected
                                                        ? 'border-highland-500 bg-highland-50 text-highland-600 shadow-md'
                                                        : 'border-soil-100 bg-white text-soil-600 hover:border-highland-100 hover:bg-highland-50 shadow-sm hover:shadow-md'
                                                }`}
                                                aria-label={`Toggle ${service.label}`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                <span>{service.label} (+{formatCurrency(service.cost)})</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Right: Estimate Output */}
                        <div className="space-y-6">
                            {/* Move Date */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <Calendar className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">Move Date</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.moveDate}
                                    onChange={(e) => handleInputChange('moveDate', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                    aria-label="Select move date"
                                    required
                                />
                            </div>

                            {/* Estimate Button */}
                            <button
                                onClick={getCostEstimate}
                                disabled={isEstimating || !formData.from || !formData.to}
                                className="w-full px-6 py-3 bg-highland-500 hover:bg-highland-600 disabled:bg-soil-400 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                aria-label="Get cost estimate"
                            >
                                {isEstimating ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        <span>Calculating...</span>
                                    </>
                                ) : (
                                    <>
                                        <DollarSign className="w-5 h-5" />
                                        <span>Get Estimate</span>
                                    </>
                                )}
                            </button>

                            {/* Estimate Output */}
                            {estimate && (
                                <div className="bg-soil-50 rounded-lg p-6 border border-soil-100">
                                    <h3 className="text-xl font-semibold text-soil-900 mb-4">Your Moving Estimate</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-soil-600">Base Cost:</span>
                                            <span className="font-medium">{formatCurrency(estimate.baseCost)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-soil-600">Distance Fee:</span>
                                            <span className="font-medium">{formatCurrency(estimate.distanceFee)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-soil-600">Additional Services:</span>
                                            <span className="font-medium">{formatCurrency(estimate.serviceFee)}</span>
                                        </div>
                                        <div className="border-t border-soil-100 pt-3 mt-3">
                                            <div className="flex justify-between">
                                                <span className="text-lg font-semibold text-soil-900">Total Estimate:</span>
                                                <span className="text-lg font-semibold text-highland-500">{formatCurrency(estimate.total)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="flex items-center space-x-2 p-4 bg-murram-100 text-murram-600 rounded-lg">
                                    <AlertCircle className="w-5 h-5" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Booking Form Section */}
            {estimate && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                        <h2 className="text-3xl font-bold text-soil-900 mb-6 text-center">Book Your Move</h2>
                        <p className="text-soil-600 text-center max-w-2xl mx-auto mb-8">
                            Ready to proceed? Provide your contact details to confirm your booking.
                        </p>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Contact Information */}
                            <div className="space-y-6">
                                <div>
                                    <label className="flex items-center space-x-2 mb-2">
                                        <Users className="w-5 h-5 text-highland-500" />
                                        <span className="text-lg font-semibold text-soil-900">Full Name</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                        placeholder="Enter your full name"
                                        className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                        aria-label="Full name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 mb-2">
                                        <Mail className="w-5 h-5 text-highland-500" />
                                        <span className="text-lg font-semibold text-soil-900">Email</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        placeholder="Enter your email"
                                        className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                        aria-label="Email address"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="flex items-center space-x-2 mb-2">
                                        <Phone className="w-5 h-5 text-highland-500" />
                                        <span className="text-lg font-semibold text-soil-900">Phone</span>
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        placeholder="Enter your phone number"
                                        className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white"
                                        aria-label="Phone number"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Additional Notes */}
                            <div>
                                <label className="flex items-center space-x-2 mb-2">
                                    <CheckCircle className="w-5 h-5 text-highland-500" />
                                    <span className="text-lg font-semibold text-soil-900">Additional Notes</span>
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    placeholder="Any special instructions or details about your move?"
                                    className="w-full px-4 py-3 border-2 border-soil-100 rounded-lg focus:ring-4 focus:ring-highland-50 focus:border-highland-500 transition-all duration-200 bg-soil-50 hover:bg-white h-32"
                                    aria-label="Additional notes"
                                />
                            </div>
                        </div>

                        {/* Additional Services (Editable in Booking Section) */}
                        <div className="mt-8">
                            <label className="flex items-center space-x-2 mb-4">
                                <CheckCircle className="w-5 h-5 text-highland-500" />
                                <span className="text-lg font-semibold text-soil-900">Additional Services</span>
                            </label>
                            <p className="text-soil-600 text-sm mb-4">
                                You can modify your additional services here before confirming your booking.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {additionalServicesOptions.map(service => {
                                    const Icon = service.icon;
                                    const isSelected = formData.additionalServices.includes(service.id);
                                    return (
                                        <button
                                            key={service.id}
                                            onClick={() => handleServiceToggle(service.id)}
                                            className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 text-sm ${
                                                isSelected
                                                    ? 'border-highland-500 bg-highland-50 text-highland-600 shadow-md'
                                                    : 'border-soil-100 bg-white text-soil-600 hover:border-highland-100 hover:bg-highland-50 shadow-sm hover:shadow-md'
                                            }`}
                                            aria-label={`Toggle ${service.label}`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span>{service.label} (+{formatCurrency(service.cost)})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Booking Submission */}
                        <div className="mt-8 flex justify-end">
                            <button
                                onClick={submitBooking}
                                disabled={isSubmitting}
                                className="px-8 py-3 bg-highland-500 hover:bg-highland-600 disabled:bg-soil-400 text-white font-semibold rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                aria-label="Confirm booking"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5" />
                                        <span>Confirm Booking</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Success Message */}
                        {bookingSuccess && (
                            <div className="mt-6 p-4 bg-highland-50 text-highland-600 rounded-lg flex items-center space-x-2">
                                <CheckCircle className="w-5 h-5" />
                                <span>{bookingSuccess.message} (Booking ID: {bookingSuccess.bookingId})</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Why Choose Us */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-soil-900 mb-4">Why Choose Rent Utopia Moving Services?</h2>
                    <p className="text-soil-600 max-w-2xl mx-auto">
                        Our professional moving services are designed to make your transition smooth, affordable, and stress-free.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        {
                            icon: Truck,
                            title: 'Professional Team',
                            desc: 'Our experienced movers handle your belongings with care and precision.'
                        },
                        {
                            icon: DollarSign,
                            title: 'Transparent Pricing',
                            desc: 'Clear, upfront estimates with no hidden fees.'
                        },
                        {
                            icon: Calendar,
                            title: 'Flexible Scheduling',
                            desc: 'Book your move at a time that suits you best.'
                        }
                    ].map((feature, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-sm border border-soil-100 p-6 text-center">
                            <feature.icon className="w-8 h-8 text-highland-500 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-soil-900 mb-2">{feature.title}</h3>
                            <p className="text-soil-600 text-sm">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MovingServices;